import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { productsAPI } from '../lib/api';
import Seo from '../components/Seo';
import ProductCard from '../components/ProductCard';
import PerforatedDivider from '../components/PerforatedDivider';
import { bestPricePerKgId } from '../lib/highlights';
import { ARTICLES } from '../data/articles';
import { SECTORS, getSector, productsForSector } from '../data/sectors';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const SITE = 'https://www.carpepel.com';

/**
 * Página de un sector (/papel-para/:slug) — la mitad "spoke" del clúster.
 *
 * Los productos NO se listan a mano: salen del catálogo real filtrado por la
 * regla de gramaje del sector (ver data/sectors.js). Si el admin sube un
 * producto nuevo, aparece aquí solo; si da uno de baja, desaparece — nunca
 * queda una página prometiendo algo que ya no se vende.
 */
export default function SectorDetail() {
    const { slug } = useParams();
    const sector = getSector(slug);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await productsAPI.getAll();
                setProducts(res.data);
            } catch {}
            setLoading(false);
        };
        load();
    }, []);

    // Un slug inventado no debe responder 200 con una página vacía: se manda
    // al hub, que sí es una página real y relevante.
    if (!sector) return <Navigate to="/papel-para" replace />;

    const matched = productsForSector(sector, products);
    // Se calcula sobre el catálogo COMPLETO, igual que en Home y Catálogo: si
    // el mejor precio por kilo no está en este sector, aquí no se marca nada.
    const bestPriceId = bestPricePerKgId(products);
    const guides = sector.relatedGuides
        .map((s) => ARTICLES.find((a) => a.slug === s))
        .filter(Boolean);
    const otros = SECTORS.filter((s) => s.slug !== sector.slug);

    return (
        <div data-testid="sector-detail-page">
            <Seo
                title={sector.seo.title}
                description={sector.seo.description}
                keywords={sector.seo.keywords}
                canonicalPath={`/papel-para/${sector.slug}`}
                schema={[
                    {
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE}/` },
                            { '@type': 'ListItem', position: 2, name: 'Por tipo de negocio', item: `${SITE}/papel-para` },
                            { '@type': 'ListItem', position: 3, name: sector.label, item: `${SITE}/papel-para/${sector.slug}` },
                        ],
                    },
                    {
                        // El FAQPage describe EXACTAMENTE el bloque visible de
                        // abajo, palabra por palabra — mismo criterio que en
                        // Catálogo: schema que no coincide con lo que el
                        // usuario ve es riesgo de acción manual, no un extra.
                        '@type': 'FAQPage',
                        mainEntity: sector.faq.map(({ q, a }) => ({
                            '@type': 'Question',
                            name: q,
                            acceptedAnswer: { '@type': 'Answer', text: a },
                        })),
                    },
                ]}
            />

            <div style={{ backgroundColor: 'var(--ink)' }} className="w-full py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-5">
                    <div
                        className="hidden md:block w-[3px] self-stretch rounded-full"
                        style={{ backgroundColor: 'var(--kraft-deep)', minHeight: '48px' }}
                    />
                    <div className="flex items-center gap-4">
                        <sector.Icon className="hidden sm:block w-8 h-8 flex-shrink-0" style={{ color: 'var(--kraft-deep)' }} />
                        <div>
                            <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-dim)' }}>
                                {sector.eyebrow}
                            </p>
                            <h1 className="font-heading text-2xl sm:text-3xl font-black text-white leading-none text-balance">
                                {sector.h1}
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
                {/* Miga de pan visible — coincide exactamente con el BreadcrumbList. */}
                <nav aria-label="Miga de pan" className="mb-8">
                    <ol className="flex items-center gap-2 flex-wrap font-mono text-[11px] uppercase tracking-[0.1em]">
                        <li><Link to="/" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Inicio</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li><Link to="/papel-para" className="transition-opacity hover:opacity-70" style={{ color: 'var(--ink-soft)' }}>Por tipo de negocio</Link></li>
                        <li aria-hidden="true" style={{ color: 'var(--line)' }}>/</li>
                        <li aria-current="page" style={{ color: 'var(--ink)' }} className="truncate max-w-[60vw]">{sector.label}</li>
                    </ol>
                </nav>

                {/* Entrada editorial: el problema del sector, y los tres hechos
                    que lo resuelven — todos verificables contra el catálogo. */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12">
                    <div className="md:col-span-7">
                        {/* `text-pretty`, no `text-balance`: balance está pensado
                            para titulares de pocas líneas —los navegadores dejan
                            de aplicarlo pasadas ~6— y este párrafo de entrada es
                            más largo que eso, así que no hacía nada. pretty sí
                            actúa en texto corrido: elimina viudas al final del
                            párrafo, que es el defecto que se veía. */}
                        <p className="text-base md:text-lg text-[var(--ink)] leading-relaxed text-pretty">
                            {sector.lead}
                        </p>
                    </div>
                    <div className="md:col-span-5 md:border-l md:border-[var(--line)] md:pl-10">
                        <ul className="space-y-3">
                            {sector.points.map((point, i) => (
                                <li key={i} className="flex gap-3 text-sm text-[var(--ink-soft)] leading-relaxed">
                                    <span className="font-mono text-[11px] flex-shrink-0 pt-0.5" style={{ color: 'var(--kraft-deep)' }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Productos que aplican, del catálogo real */}
                {/* flex-wrap + gap: a 390px el titular y el enlace se pisaban
                    en la misma línea y el titular quedaba estrujado. */}
                <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 mb-6">
                    <h2 className="font-heading text-base md:text-lg font-bold uppercase tracking-wide text-[var(--ink)]">
                        Lo que te sirve del catálogo
                    </h2>
                    <Link to="/catalogo" className="text-sm font-medium text-[var(--kraft-deep)] hover:opacity-70 flex items-center gap-1 whitespace-nowrap">
                        Ver catálogo completo <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-64 bg-[var(--line)] animate-pulse rounded-sm" />
                        ))}
                    </div>
                ) : matched.length === 0 ? (
                    <p className="text-sm text-[var(--ink-soft)] py-8">
                        En este momento no hay producto disponible para este uso.{' '}
                        <Link to="/catalogo" className="underline" style={{ color: 'var(--kraft-deep)' }}>
                            Revisa el catálogo completo
                        </Link>.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {matched.map((product, i) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                index={i}
                                ribbon={product.id === bestPriceId ? 'Mejor precio por kilo' : null}
                            />
                        ))}
                    </div>
                )}

                <PerforatedDivider className="my-12" />

                {/* FAQ — texto idéntico al del FAQPage schema */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                    <div className="md:col-span-4">
                        <p className="font-mono text-[11px] uppercase tracking-widest mb-3" style={{ color: 'var(--kraft-deep)' }}>
                            Preguntas frecuentes
                        </p>
                        <h2 className="font-heading text-2xl font-black text-[var(--ink)] leading-[1.05] tracking-tight text-balance">
                            Lo que más nos preguntan de este giro
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        {sector.faq.map(({ q, a }, i) => (
                            <div key={i} className="py-5 border-b border-[var(--line)] last:border-b-0">
                                <h3 className="font-heading text-base font-bold text-[var(--ink)] mb-2 text-balance">{q}</h3>
                                <p className="text-sm text-[var(--ink-soft)] leading-relaxed text-pretty">{a}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {guides.length > 0 && (
                    <>
                        <PerforatedDivider className="my-12" />
                        <h2 className="font-heading text-base font-bold uppercase tracking-wide text-[var(--ink)] mb-5">
                            Para profundizar
                        </h2>
                        <div className="border-t border-[var(--line)]">
                            {guides.map((article) => (
                                <Link
                                    key={article.slug}
                                    to={`/guias/${article.slug}`}
                                    data-testid={`sector-guide-${article.slug}`}
                                    className="group flex items-baseline justify-between gap-6 py-4 border-b border-[var(--line)] transition-colors duration-300"
                                >
                                    <h3 className="font-heading text-base font-bold text-[var(--ink)] leading-snug text-balance group-hover:text-[var(--kraft-deep)] transition-colors duration-300">
                                        {article.title}
                                    </h3>
                                    <ArrowRight className="w-4 h-4 flex-shrink-0 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {/* Enlace lateral entre spokes: cada sector enlaza a los otros
                    cuatro, no solo de vuelta al hub. */}
                <PerforatedDivider className="my-12" />
                <h2 className="font-heading text-base font-bold uppercase tracking-wide text-[var(--ink)] mb-5">
                    Otros giros
                </h2>
                <div className="flex flex-wrap gap-3">
                    {otros.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/papel-para/${s.slug}`}
                            data-testid={`sector-cross-${s.slug}`}
                            className="inline-flex items-center gap-2 border border-[var(--line)] rounded-sm px-4 py-2.5 text-sm text-[var(--ink)] transition-colors duration-200 hover:border-[var(--kraft)]"
                            style={{ backgroundColor: 'var(--paper-2)' }}
                        >
                            <s.Icon className="w-4 h-4" style={{ color: 'var(--kraft-deep)' }} />
                            {s.label}
                        </Link>
                    ))}
                </div>

                <div className="mt-10">
                    <Link
                        to="/papel-para"
                        data-testid="sector-back"
                        className="inline-flex items-center gap-2 text-sm transition-opacity duration-200 hover:opacity-70"
                        style={{ color: 'var(--ink-soft)' }}
                    >
                        <ArrowLeft className="w-4 h-4" /> Todos los giros
                    </Link>
                </div>
            </div>

            {/* Cierre comercial, mismo patrón que el CTA de Home */}
            <section style={{ backgroundColor: 'var(--ink)' }} className="on-ink" data-testid="sector-cta">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div>
                        <h2 className="font-heading text-base md:text-lg font-bold uppercase tracking-wide text-white mb-2">
                            ¿Cotizas por volumen?
                        </h2>
                        <p className="text-sm" style={{ color: 'var(--ink-dim)' }}>
                            Precios especiales por volumen y envío a toda la República Mexicana.
                        </p>
                    </div>
                    <Link to="/catalogo" className="flex-shrink-0">
                        <Button
                            className="rounded-sm h-12 px-8 uppercase font-bold tracking-widest text-sm whitespace-nowrap"
                            style={{ backgroundColor: 'var(--kraft)', color: 'var(--ink)' }}
                        >
                            Ver precios
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
